// src/components/games/crossroads/ModalChoiceDialog.tsx
import { logger } from '../../../utils/logger';
// Modal dialog for player choices in Crossroads TCG

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonText,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonRadioGroup,
  IonRadio,
} from '@ionic/react';
import { ModalChoice, ModalChoiceType } from './types';
import './ModalChoiceDialog.css';

interface ModalChoiceDialogProps {
  modalChoice: ModalChoice | null;
  isOpen: boolean;
  onChoiceComplete?: () => void; // Called after choice is made to trigger re-render
}

export const ModalChoiceDialog: React.FC<ModalChoiceDialogProps> = ({
  modalChoice,
  isOpen,
  onChoiceComplete,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  // Debug logging
  useEffect(() => {
    if (isOpen && modalChoice) {
      logger.log('🎨 ModalChoiceDialog RENDERING:', {
        isOpen,
        title: modalChoice.title,
        type: modalChoice.type,
        optionsCount: modalChoice.options?.length || 0,
        hasDescription: !!modalChoice.description
      });
    }
  }, [isOpen, modalChoice]);

  // Reset selection when modal opens or changes
  useEffect(() => {
    setSelectedOptions([]);
  }, [modalChoice?.id]);

  if (!modalChoice) return null;

  const { type, title, description, options = [], minChoices = 1, maxChoices = 1, revealedCards = [] } = modalChoice;

  const handleOptionToggle = (optionId: string) => {
    if (type === ModalChoiceType.BINARY || type === ModalChoiceType.CHOOSE_ONE) {
      // Single selection
      setSelectedOptions([optionId]);
    } else if (type === ModalChoiceType.CHOOSE_MULTIPLE) {
      // Multiple selection
      if (selectedOptions.includes(optionId)) {
        setSelectedOptions(selectedOptions.filter(id => id !== optionId));
      } else {
        if (selectedOptions.length < maxChoices) {
          setSelectedOptions([...selectedOptions, optionId]);
        }
      }
    }
  };

  const handleConfirm = () => {
    if (modalChoice.onComplete) {
      modalChoice.onComplete(selectedOptions);
    }
    // Trigger parent re-render after choice is completed
    if (onChoiceComplete) {
      onChoiceComplete();
    }
  };

  const handleCancel = () => {
    if (modalChoice.onCancel) {
      modalChoice.onCancel();
    }
    // Trigger parent re-render after cancel
    if (onChoiceComplete) {
      onChoiceComplete();
    }
  };

  const canConfirm = type === ModalChoiceType.REVEAL_INFO ||
    type === ModalChoiceType.CONFIRM ||
    (selectedOptions.length >= minChoices && selectedOptions.length <= maxChoices);

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false} className="modal-choice-dialog">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {description && (
          <IonText>
            <p style={{ marginBottom: '16px', fontSize: '14px' }}>{description}</p>
          </IonText>
        )}

        {(() => {
          const shouldRenderOptions = type === ModalChoiceType.BINARY || type === ModalChoiceType.CHOOSE_ONE;
          logger.log('🔍 Options Rendering Check:', {
            type,
            shouldRenderOptions,
            optionsLength: options.length,
            options: options.map(o => ({ id: o.id, label: o.label }))
          });
          return shouldRenderOptions;
        })() && (
          <IonRadioGroup
            value={selectedOptions[0] || ''}
            onIonChange={(e) => handleOptionToggle(e.detail.value)}
          >
            <IonList>
              {options.map((option) => (
                <IonItem key={option.id} disabled={option.disabled}>
                  <IonLabel>
                    <h3>{option.label}</h3>
                    {option.description && <p>{option.description}</p>}
                  </IonLabel>
                  <IonRadio slot="start" value={option.id} />
                </IonItem>
              ))}
            </IonList>
          </IonRadioGroup>
        )}

        {type === ModalChoiceType.CHOOSE_MULTIPLE && (
          <IonList>
            <IonText color="medium">
              <p style={{ fontSize: '12px', marginBottom: '8px' }}>
                Choose {minChoices === maxChoices ? minChoices : `${minChoices}-${maxChoices}`}{' '}
                option{maxChoices > 1 ? 's' : ''}
              </p>
            </IonText>
            {options.map((option) => (
              <IonItem key={option.id} disabled={option.disabled}>
                <IonCheckbox
                  slot="start"
                  checked={selectedOptions.includes(option.id)}
                  onIonChange={() => handleOptionToggle(option.id)}
                  disabled={
                    option.disabled ||
                    (!selectedOptions.includes(option.id) && selectedOptions.length >= maxChoices)
                  }
                />
                <IonLabel>
                  <h3>{option.label}</h3>
                  {option.description && <p>{option.description}</p>}
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        {type === ModalChoiceType.REVEAL_INFO && (
          <div>
            {revealedCards.length === 0 ? (
              <IonText color="medium">
                <p>No cards to display</p>
              </IonText>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                {revealedCards.map((card) => (
                  <div
                    key={card.id}
                    style={{
                      width: '120px',
                      textAlign: 'center',
                      border: '1px solid var(--ion-color-medium)',
                      borderRadius: '8px',
                      padding: '8px',
                      backgroundColor: 'var(--ion-color-light)',
                    }}
                  >
                    {card.imageUrl && (
                      <img
                        src={card.imageUrl}
                        alt={card.name}
                        style={{
                          width: '100%',
                          height: '160px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          marginBottom: '8px',
                        }}
                      />
                    )}
                    <IonText>
                      <p style={{ fontSize: '12px', fontWeight: 'bold', margin: 0 }}>{card.name}</p>
                    </IonText>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fallback for unknown modal types or missing options */}
        {!type && options.length > 0 && (
          <IonRadioGroup
            value={selectedOptions[0] || ''}
            onIonChange={(e) => handleOptionToggle(e.detail.value)}
          >
            <IonList>
              {options.map((option) => (
                <IonItem key={option.id} disabled={option.disabled}>
                  <IonLabel>
                    <h3>{option.label}</h3>
                    {option.description && <p>{option.description}</p>}
                  </IonLabel>
                  <IonRadio slot="start" value={option.id} />
                </IonItem>
              ))}
            </IonList>
          </IonRadioGroup>
        )}

        <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
          <IonButton
            expand="block"
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{ flex: 1 }}
            color={type === ModalChoiceType.CONFIRM ? 'warning' : undefined}
          >
            {type === ModalChoiceType.REVEAL_INFO ? 'OK' : 'Confirm'}
          </IonButton>
          {modalChoice.onCancel && (
            <IonButton expand="block" fill="outline" onClick={handleCancel} style={{ flex: 1 }}>
              Cancel
            </IonButton>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};
